import React, { useState, useContext } from 'react';
import styled from 'styled-components';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faUser,
  faEnvelope,
  faCalendar,
  faCloud,
  faEdit,
  faSave,
  faTimes,
  faPalette,
  faGlobe
} from '@fortawesome/free-solid-svg-icons';
import { SettingsContext } from '../contexts/SettingsContext';
import { createOrUpdateUserProfile } from '../utils/firestore';

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  backdrop-filter: blur(4px);
  -webkit-backdrop-filter: blur(4px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: var(--space-4);
`;

const ModalContent = styled.div`
  background: var(--surface-color);
  border-radius: var(--radius-xl);
  box-shadow: var(--shadow-xl);
  max-width: 500px;
  width: 100%;
  max-height: 90vh;
  overflow-y: auto;
  border: 1px solid var(--border-color-light);
  position: relative;
`;

const ModalHeader = styled.div`
  padding: var(--space-6);
  border-bottom: 1px solid var(--border-color-light);
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const ModalTitle = styled.h2`
  font-size: var(--text-xl);
  font-weight: 600;
  color: var(--text-color);
  margin: 0;
  font-family: var(--font-heading-bold);
  display: flex;
  align-items: center;
  gap: var(--space-3);
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  color: var(--text-secondary);
  cursor: pointer;
  padding: var(--space-2);
  border-radius: var(--radius-lg);
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);

  &:hover {
    background: var(--neutral-100);
    color: var(--text-color);
  }
`;

const ModalBody = styled.div`
  padding: var(--space-6);
`;

const ProfileSection = styled.div`
  margin-bottom: var(--space-6);
`;

const SectionTitle = styled.h3`
  font-size: var(--text-lg);
  font-weight: 600;
  color: var(--text-color);
  margin: 0 0 var(--space-4) 0;
  font-family: var(--font-heading-medium);
  display: flex;
  align-items: center;
  gap: var(--space-2);
`;

const ProfileAvatar = styled.div`
  width: 80px;
  height: 80px;
  border-radius: var(--radius-full);
  background: linear-gradient(135deg, var(--primary-600), var(--primary-700));
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: var(--text-2xl);
  font-weight: 600;
  margin: 0 auto var(--space-4) auto;
  border: 3px solid var(--primary-100);
`;

const ProfileInfo = styled.div`
  display: grid;
  gap: var(--space-4);
`;

const InfoItem = styled.div`
  display: flex;
  align-items: center;
  gap: var(--space-3);
  padding: var(--space-4);
  background: var(--background-color);
  border-radius: var(--radius-lg);
  border: 1px solid var(--border-color-light);
`;

const InfoIcon = styled.div`
  width: 40px;
  height: 40px;
  border-radius: var(--radius-lg);
  background: var(--primary-50);
  color: var(--primary-600);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`;

const InfoContent = styled.div`
  flex: 1;
`;

const InfoLabel = styled.div`
  font-size: var(--text-sm);
  color: var(--text-secondary);
  margin-bottom: var(--space-1);
  font-weight: 500;
`;

const InfoValue = styled.div`
  font-size: var(--text-base);
  color: var(--text-color);
  font-weight: 500;
`;

const EditableValue = styled.input`
  font-size: var(--text-base);
  color: var(--text-color);
  font-weight: 500;
  border: 1px solid var(--border-color-light);
  border-radius: var(--radius-md);
  padding: var(--space-2) var(--space-3);
  background: var(--surface-color);
  width: 100%;

  &:focus {
    outline: none;
    border-color: var(--primary-600);
    box-shadow: 0 0 0 3px var(--primary-100);
  }
`;

const ModalFooter = styled.div`
  padding: var(--space-6);
  border-top: 1px solid var(--border-color-light);
  display: flex;
  justify-content: flex-end;
  gap: var(--space-3);
`;

const ActionButton = styled.button`
  padding: var(--space-3) var(--space-5);
  border-radius: var(--radius-lg);
  font-weight: 500;
  font-size: var(--text-sm);
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  display: flex;
  align-items: center;
  gap: var(--space-2);
  border: 1px solid transparent;

  &.primary {
    background: var(--primary-600);
    color: white;
    border-color: var(--primary-600);

    &:hover:not(:disabled) {
      background: var(--primary-700);
      border-color: var(--primary-700);
      transform: translateY(-1px);
      box-shadow: var(--shadow-md);
    }
  }

  &.secondary {
    background: var(--surface-color);
    color: var(--text-color);
    border-color: var(--border-color-light);

    &:hover:not(:disabled) {
      background: var(--background-color);
      transform: translateY(-1px);
      box-shadow: var(--shadow-sm);
    }
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
  }
`;

const UserProfileModal = ({ isOpen, onClose, user, userProfile }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editedData, setEditedData] = useState({});
  const [loading, setLoading] = useState(false);

  React.useEffect(() => {
    if (userProfile) {
      setEditedData({
        displayName: userProfile.displayName || '',
        email: userProfile.email || user?.email || ''
      });
    }
  }, [userProfile, user]);

  const handleSave = async () => {
    if (!user?.uid) return;

    setLoading(true);
    try {
      await createOrUpdateUserProfile(user.uid, editedData);
      setIsEditing(false);
    } catch (error) {
      console.error('Error updating profile:', error);
    } finally {
      setLoading(false);
    }
  };

  const getInitials = (name) => {
    return name
      ? name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2)
      : 'U';
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return 'N/A';
    return new Date(timestamp.seconds * 1000).toLocaleDateString('he-IL');
  };

  if (!isOpen) return null;

  return (
    <ModalOverlay onClick={onClose}>
      <ModalContent onClick={e => e.stopPropagation()}>
        <ModalHeader>
          <ModalTitle>
            <FontAwesomeIcon icon={faUser} />
            User Profile
          </ModalTitle>
          <CloseButton onClick={onClose}>
            <FontAwesomeIcon icon={faTimes} />
          </CloseButton>
        </ModalHeader>

        <ModalBody>
          <ProfileSection>
            <ProfileAvatar>
              {getInitials(editedData.displayName || userProfile?.displayName)}
            </ProfileAvatar>
          </ProfileSection>

          <ProfileSection>
            <SectionTitle>
              <FontAwesomeIcon icon={faUser} />
              Personal Information
            </SectionTitle>
            <ProfileInfo>
              <InfoItem>
                <InfoIcon>
                  <FontAwesomeIcon icon={faUser} />
                </InfoIcon>
                <InfoContent>
                  <InfoLabel>Display Name</InfoLabel>
                  {isEditing ? (
                    <EditableValue
                      value={editedData.displayName}
                      onChange={(e) => setEditedData(prev => ({ ...prev, displayName: e.target.value }))}
                      placeholder="Enter your name"
                    />
                  ) : (
                    <InfoValue>{userProfile?.displayName || 'Not set'}</InfoValue>
                  )}
                </InfoContent>
              </InfoItem>

              <InfoItem>
                <InfoIcon>
                  <FontAwesomeIcon icon={faEnvelope} />
                </InfoIcon>
                <InfoContent>
                  <InfoLabel>Email Address</InfoLabel>
                  <InfoValue>{userProfile?.email || user?.email || 'Not set'}</InfoValue>
                </InfoContent>
              </InfoItem>

              <InfoItem>
                <InfoIcon>
                  <FontAwesomeIcon icon={faCalendar} />
                </InfoIcon>
                <InfoContent>
                  <InfoLabel>Member Since</InfoLabel>
                  <InfoValue>{formatDate(userProfile?.createdAt)}</InfoValue>
                </InfoContent>
              </InfoItem>

              <InfoItem>
                <InfoIcon>
                  <FontAwesomeIcon icon={faCloud} />
                </InfoIcon>
                <InfoContent>
                  <InfoLabel>Storage Used</InfoLabel>
                  <InfoValue>{userProfile?.storageUsed ? `${(userProfile.storageUsed / 1024 / 1024).toFixed(2)} MB` : '0 MB'}</InfoValue>
                </InfoContent>
              </InfoItem>
            </ProfileInfo>
          </ProfileSection>
        </ModalBody>

        <ModalFooter>
          {isEditing ? (
            <>
              <ActionButton className="secondary" onClick={() => setIsEditing(false)}>
                Cancel
              </ActionButton>
              <ActionButton className="primary" onClick={handleSave} disabled={loading}>
                <FontAwesomeIcon icon={faSave} />
                {loading ? 'Saving...' : 'Save'}
              </ActionButton>
            </>
          ) : (
            <>
              <ActionButton className="secondary" onClick={onClose}>
                Close
              </ActionButton>
              <ActionButton className="primary" onClick={() => setIsEditing(true)}>
                <FontAwesomeIcon icon={faEdit} />
                Edit
              </ActionButton>
            </>
          )}
        </ModalFooter>
      </ModalContent>
    </ModalOverlay>
  );
};

export default UserProfileModal;
